import { Account, User } from 'model/IChat';
import {
  forceTokenRemoval,
  shouldCheckForTooLargeCookies,
} from 'modules/authentication/helpers/cookies';
import { requestOptionsWithSessionToken } from 'modules/authentication/helpers/requestHelper';
import { unstable_batchedUpdates } from 'react-dom';
import { TrackJS } from 'trackjs';
import useAuthStore, { AuthState } from '~/authentication/store';

export const updateAuthState = (response: {
  user: User;
  spy?: { userId: string };
  token: string;
}) => {
  unstable_batchedUpdates(() => {
    useAuthStore.getState().setUser(response.user);
    useAuthStore.getState().setSpy(response?.spy);
    useAuthStore.getState().setToken(response.token);
  });
};

export const fetchAuthToken = async (token: string | undefined) => {
  try {
    const requestOptions: RequestInit = requestOptionsWithSessionToken(
      {
        method: 'POST',
      },
      token,
    );

    const tokenResponse = await fetch(
      `${URL_AUTHENTICATION_HOST}/api/v1/auth/token`,
      requestOptions,
    );

    const tokenJsonResponse = await tokenResponse.json();

    if (tokenJsonResponse.token) {
      updateAuthState(tokenJsonResponse);
    } else {
      if (useAuthStore.getState().token) {
        console.error('No valid token received, logging out');
        useAuthStore.getState().logout();
      }
    }
  } catch (exception) {
    console.warn('Error verifying token', exception);

    if (shouldCheckForTooLargeCookies()) {
      forceTokenRemoval(exception, true);
      return;
    }
  }
};

export const fetchAccount = async (
  token: string | undefined,
): Promise<boolean> => {
  try {
    const requestOptions: RequestInit = requestOptionsWithSessionToken(
      {
        method: 'GET',
      },
      token,
    );

    const accountResponse = await fetch(
      `${URL_API_HOST}/api/v1/account`,
      requestOptions,
    );

    if (accountResponse.status !== 200) {
      throw new Error('account');
    }

    const accountJson: Account = await accountResponse.json();

    useAuthStore.setState({
      account: accountJson,
    });
    if (TrackJS && accountJson?.id) {
      TrackJS.addMetadata('account_id', accountJson?.id);
    }
    return true;
  } catch (exception) {
    console.warn(`Error fetching ${exception.message}`);
  }
  return false;
};

export const fetchRealAccount = async (token: string | undefined) => {
  const requestOptions: RequestInit = requestOptionsWithSessionToken(
    {
      method: 'GET',
    },
    token,
  );

  try {
    const realAccountResponse = await fetch(
      `${URL_API_HOST}/api/v1/account/me`,
      requestOptions,
    );

    if (realAccountResponse.status !== 200) {
      throw new Error('real account');
    }

    const realAccountJson = await realAccountResponse.json();

    useAuthStore.setState({ realAccount: realAccountJson });
  } catch (exception) {
    console.warn(`Error fetching ${exception.message}`);
  }
};

export const fetchAccountInformation = async (
  state: AuthState | null | undefined,
  options: {
    silent: boolean;
  } = { silent: false },
): Promise<boolean> => {
  if (!options.silent) {
    useAuthStore.setState({ loading: true });
  }

  await fetchAuthToken(state?.token);
  await fetchAccount(useAuthStore.getState()?.token).then((success) => {
    if (success) {
      // do not await this, as it is not critical for the user experience
      void fetchRealAccount(useAuthStore.getState()?.token);
    }
  });

  useAuthStore.setState({ loading: false });
  return true;
};
